// src/utils/logger.js
class Logger {
  static log(level, message, metadata = {}) {
    const timestamp = new Date().toISOString();

    // Remove sensitive data from metadata
    const sanitizedMetadata = this.sanitizeMetadata(metadata);

    // Log to console in development
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[${level.toUpperCase()}] ${timestamp}:`, message, sanitizedMetadata);
    }
  }

  static info(message, metadata = {}) {
    this.log('info', message, metadata);
  }

  static warn(message, metadata = {}) {
    this.log('warn', message, metadata);
  }

  static error(message, metadata = {}) {
    this.log('error', message, metadata);
  }

  static debug(message, metadata = {}) {
    if (process.env.NODE_ENV !== 'production') {
      this.log('debug', message, metadata);
    }
  }

  /**
   * Sanitizes metadata to remove sensitive information
   * @param {object} metadata - The metadata to sanitize
   * @returns {object} - Sanitized metadata
   */
  static sanitizeMetadata(metadata) {
    const sanitized = { ...metadata };

    // Enhanced sensitive fields list
    const sensitiveFields = [
      'password', 'token', 'accessToken', 'refreshToken', 'secret', 'key',
      'authorization', 'cookie', 'session', 'auth', 'credentials', 'api_key',
      'apiKey', 'userToken', 'bearer', 'x-api-key', 'x-auth-token', 'jwt',
      'oauth', 'sso', 'authToken', 'auth_token', 'access_token', 'refresh_token'
    ];

    // Sanitize direct matches
    for (const field of sensitiveFields) {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }

      // Case-insensitive check
      for (const key in sanitized) {
        if (key.toLowerCase().includes(field)) {
          sanitized[key] = '[REDACTED]';
        }
      }
    }

    // Sanitize potentially sensitive values in any field
    for (const [key, value] of Object.entries(sanitized)) {
      if (typeof value === 'string') {
        // Look for patterns that might contain tokens/keys
        if (value.length > 20 && /[A-Za-z0-9_-]{20,}/.test(value)) {
          sanitized[key] = '[REDACTED_LONG_VALUE]';
        }

        // Look for common token patterns
        if (value.length > 15 && /[A-Za-z0-9_-]{15,}/.test(value) && value.includes('.')) {
          // JWT-like pattern (header.payload.signature)
          sanitized[key] = '[REDACTED_JWT]';
        }
      }
    }

    return sanitized;
  }
}

export default Logger;